import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Popconfirm } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { evoConsolidation } from '$api'
import { evoClient, putPart } from '$api/client'
import { fetchAllServices, postFcService } from '$api/evoAPIs'
import { updateStatusAttributes } from '$utils'
import StatusPage from '../Defaultwizard/StatusPage'
import StepOneFeasibilitySelection from './StepOneFeasibilitySelection'
import StepThreeSummary from '../Defaultwizard/StepThreeSummary'
import {
  StyledStepButtons,
  StyledSteps,
} from '../Defaultwizard/styledComponents'

const FeasibilityCheck = (props) => {
  const { t } = useTranslation()
  const [fcData] = useState({
    not_feasible_parts: [],
    feasible_parts: [],
  })
  const [bookableParts, setBookableParts] = useState()
  const [steps, setSteps] = useState([
    {
      title: t('service.selection'),
      content: '',
    },
    {
      title: t('service.order'),
      content: '',
    },
  ])
  const [current, setCurrent] = useState(0)
  const { message } = App.useApp()
  const [visible, setVisible] = useState(true)
  const [loading, setLoading] = useState(false)
  const [buttonActive, setButtonActive] = useState(true)
  const [configState, setConfigState] = useState(true)
  const queryClient = useQueryClient()
  const { data: servicesData } = useQuery(['services'], fetchAllServices)

  const addBookedFeasibilityChecks = useMutation(
    async (bookablePart) => {
      try {
        await updateClientPartStatus.mutateAsync({
          partId: bookablePart.id,
          partData: bookablePart,
        })

        const enrichedAttributes = [...bookablePart.attributes]
        const hasMaterialAttribute = enrichedAttributes.some(
          (attr) => attr.key === 'Material',
        )

        if (bookablePart.client_material?.name && !hasMaterialAttribute) {
          enrichedAttributes.push({
            key: 'Material',
            value: bookablePart.client_material.name,
            description: 'Mapped from ClientMaterial for service booking',
          })
        }

        const feasibilitycheckbody = {
          service_id: (
            servicesData.find((x) => x.name === 'Feasibility Check') || {}
          ).id,
          part: {
            name: bookablePart.name,
            id_part_client: bookablePart.id_part_client,
            db_id_client: bookablePart.id,
            company: bookablePart.company,
            attributes: enrichedAttributes,
            float_attributes: bookablePart.float_attributes,
            status_attributes: bookablePart.status_attributes,
            files: [],
            client_material_name: bookablePart.client_material?.name || null,
            dwh_materials: bookablePart.mapped_dwh_material_ids || [],
          },
          partlist_name: props.activeGroupName,
        }

        const response = await evoConsolidation.post(
          postFcService,
          feasibilitycheckbody,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )

        return response.data
      } catch (err) {
        setLoading(false)
        message.error(
          err.response?.data?.detail ||
            'An error occurred while booking the feasibility check.',
        )
        throw err
      }
    },
    {
      onSuccess: (data) => {
        setLoading(false)

        // Store the part ID locally in case it's not updated
        const arrOfNotUpdatedIds = localStorage.getItem('notupdatedfc')
          ? JSON.parse(localStorage.getItem('notupdatedfc'))
          : []

        if (arrOfNotUpdatedIds && arrOfNotUpdatedIds.length > 0) {
          arrOfNotUpdatedIds.push(data.id)
          localStorage.setItem(
            'notupdatedfc',
            JSON.stringify(arrOfNotUpdatedIds),
          )
        } else {
          const idArr = [data.id]
          localStorage.setItem('notupdatedfc', JSON.stringify(idArr))
        }
      },
      onError: (err) => {
        // Handle error during the mutation process
        message.error(
          err.response?.data?.detail ||
            'An error occurred while booking the feasibility check.',
        )
        setLoading(false)
      },
    },
  )

  const updateClientPartStatus = useMutation(
    ({ partId, partData }) => {
      const editedPart = updateStatusAttributes(
        partData,
        'status_attributes',
        'Feasibility Check',
        '1',
      )
      return evoClient.put(`${putPart}${partId}`, editedPart, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['allgroups'])
        queryClient.invalidateQueries(['notifications'])
      },
      onError: (err) => {
        message.error(`${err.response?.status}, Could not update part status`)
      },
    },
  )

  // Functions

  const updateSteps = useCallback(() => {
    if (bookableParts || servicesData) {
      const newSteps = [
        {
          title: t('service.selection'),
          content: (
            <StepOneFeasibilitySelection
              fcData={fcData}
              selectedParts={props.selectedParts}
              setBookableParts={setBookableParts}
              bookableParts={bookableParts}
              servicesData={servicesData}
              bookedServiceStatus={props.bookedServiceStatus}
              setConfigState={setConfigState}
            />
          ),
        },
        {
          title: t('service.order'),
          content: (
            <StepThreeSummary
              fcData={fcData}
              selectedParts={props.selectedParts}
              bookableParts={bookableParts}
              setButtonActive={setButtonActive}
              bookedServiceStatus={props.bookedServiceStatus}
            />
          ),
        },
      ]
      setSteps(newSteps)
    }
  }, [
    bookableParts,
    servicesData,
    fcData,
    props.selectedParts,
    setBookableParts,
    setButtonActive,
    props.bookedServiceStatus,
    t,
  ])

  const handleOk = () => {
    setCurrent(0)
    props.setSelectedParts([])
    setButtonActive(true)

    //post to endpoint
    for (let bookablePart of bookableParts.bookableParts) {
      addBookedFeasibilityChecks.mutate(bookablePart)
    }
    setLoading(true)
    setVisible(false)
  }

  const next = () => {
    if (current === 0) {
      setConfigState(false)
    }
    setCurrent(current + 1)
  }
  const prev = () => {
    if (current === 1) {
      setConfigState(true)
    }
    setCurrent(current - 1)
  }
  const items = steps
    .filter(function (x) {
      return x !== null
    })
    .map((item) => ({
      key: item.title,
      title: item.title,
    }))

  useEffect(() => {
    updateSteps()
  }, [updateSteps])

  return (
    <>
      {visible && (
        <>
          <h1 style={{ margin: '50px 0' }}>
            {t(props.bookedServiceStatus[1].servicename)}
          </h1>
          <div className="contentcard">
            <StyledSteps current={current} items={items} type="navigation" />
            <div style={{ margin: '50px 0px' }}>{steps[current].content}</div>
            <StyledStepButtons>
              {current < steps.length - 1 && (
                <Popconfirm
                  disabled={configState}
                  placement="topRight"
                  description={
                    <>
                      Some parts need configuration before proceeding.
                      <br />
                      Check the <i>status</i> of your part.
                    </>
                  }
                  title={<h4>Some changes have to be made</h4>}
                >
                  <Button
                    type="primary"
                    onClick={() => next()}
                    disabled={!configState}
                  >
                    {t('button.next')}
                  </Button>
                </Popconfirm>
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  onClick={() => handleOk()}
                  disabled={!buttonActive}
                >
                  {t('button.order')}
                </Button>
              )}
              {current > 0 && (
                <Button
                  style={{
                    margin: '0 8px',
                  }}
                  onClick={() => prev()}
                >
                  {t('button.back')}
                </Button>
              )}
            </StyledStepButtons>
          </div>
        </>
      )}
      {!visible && (
        <>
          <StatusPage
            setIsBookModalOpen={props.setIsBookModalOpen}
            loading={loading}
            setCurrent={props.setCurrent}
          />
        </>
      )}
    </>
  )
}
export default FeasibilityCheck
